/**
 * JobBanner — atividade de onboarding no topo do /admin/ztna.
 *
 * Em progresso: banner teal com barra + LOG AO VIVO (linhas via SSE, store
 * ztnaJobLogStore). Terminal: a MESMA caixa vira o CARD DE RESULTADO in-place
 * (sucesso verde / falha vermelha / validação amarela) e permanece até dispensar.
 *
 * Fontes (aditivas): SSE (`finished` prop, imediato) OU o polling de descoberta
 * (`/jobs?status=running|pending` + `getJob`, fallback quando o SSE cai).
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useSeguirOFim } from '@/hooks/useSeguirOFim'
import { Anchor, Button, Code, Group, Loader, Progress, ScrollArea, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlayerStop, IconCircleCheck, IconAlertTriangle, IconClock } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ztnaApi } from '../api'
import type { JobResponse, JobStatus } from '../types'
import type { JobFinished } from '../useZtnaJobRealtime'
import { ztnaJobLogStore } from '../ztnaJobLogStore'

const POLL_INTERVAL_MS = 5_000

const TERMINAL_STATUSES: ReadonlySet<JobStatus> = new Set([
  'success',
  'failed',
  'cancelled',
  'needs_validation',
])

interface Props {
  jobId?: number | null
  finished?: JobFinished | null
  onDismiss?: () => void
}

interface SubProgress {
  elapsed_seconds?: number
  expected_seconds?: number
  attempt?: number
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0s'
  if (seconds < 60) return `${Math.floor(seconds)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

export function JobBanner({ jobId: jobIdProp, finished, onDismiss }: Props) {
  const queryClient = useQueryClient()
  const [shownTerminal, setShownTerminal] = useState<number | null>(null)
  const [dismissedJobId, setDismissedJobId] = useState<number | null>(null)
  const previousStatusRef = useRef<JobStatus | null>(null)

  // Discovery: se nao passou jobId, busca o job ativo.
  //
  // `pending_gmud` entra aqui de proposito: sem ele, um job que espera
  // aprovacao ficava INVISIVEL depois de recarregar a pagina — o operador via a
  // app inalterada, sem nenhuma pista de que ja havia pedido a mudanca, e
  // pedia de novo. E' a ultima consulta porque `running` e `pending` sao
  // trabalho em curso, que tem prioridade visual sobre trabalho parado.
  const discoveryQuery = useQuery({
    queryKey: ['ztna', 'jobs', 'active'],
    queryFn: async () => {
      const list = await ztnaApi.listJobs('running')
      if (list.items.length > 0) return list.items[0]
      const pending = await ztnaApi.listJobs('pending')
      if (pending.items.length > 0) return pending.items[0]
      const aguardando = await ztnaApi.listJobs('pending_gmud')
      return aguardando.items[0] ?? null
    },
    enabled: jobIdProp == null,
    refetchInterval: jobIdProp == null ? POLL_INTERVAL_MS : false,
    staleTime: 0,
  })

  const effectiveJobId = jobIdProp ?? discoveryQuery.data?.id ?? null

  const jobQuery = useQuery({
    queryKey: ['ztna', 'jobs', effectiveJobId],
    queryFn: () => ztnaApi.getJob(effectiveJobId!),
    enabled: effectiveJobId != null,
    refetchInterval: (query) => {
      const data = query.state.data as JobResponse | undefined
      if (!data) return POLL_INTERVAL_MS
      if (TERMINAL_STATUSES.has(data.status)) return false
      return POLL_INTERVAL_MS
    },
  })

  const cancelMut = useMutation({
    mutationFn: (id: number) => ztnaApi.cancelJob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ztna', 'jobs'] })
      notifications.show({
        title: 'Cancelamento solicitado',
        message: 'Worker iniciou rollback dos recursos AWS criados.',
        color: 'orange',
      })
    },
    onError: (err: Error) => {
      notifications.show({ title: 'Falha ao cancelar', message: err.message, color: 'red' })
    },
  })

  const job = jobQuery.data ?? null

  // Linhas de log ao vivo (SSE) do job corrente.
  const logJobId = finished?.jobId ?? job?.id ?? null
  const logLines = useSyncExternalStore(ztnaJobLogStore.subscribe, () =>
    ztnaJobLogStore.get(logJobId),
  )
  // Linha nova rola o log para o fim, como num terminal (consoleai#25060).
  const seguirOFim = useSeguirOFim<HTMLDivElement>(logLines)

  // Reforço na transição terminal detectada por polling (o SSE já invalida as
  // queries; aqui garantimos o refresh mesmo sem SSE).
  useEffect(() => {
    if (!job) return
    const prev = previousStatusRef.current
    previousStatusRef.current = job.status
    if (TERMINAL_STATUSES.has(job.status) && prev != null && prev !== job.status) {
      if (shownTerminal === job.id) return
      setShownTerminal(job.id)
      queryClient.invalidateQueries({ queryKey: ['ztna', 'apps'] })
      queryClient.invalidateQueries({ queryKey: ['ztna', 'kpis'] })
      queryClient.invalidateQueries({ queryKey: ['ztna', 'onboarding-history'] })
    }
  }, [job, queryClient, shownTerminal])

  // ── Resultado terminal (SSE tem prioridade; senão deriva do job polled) ──
  const polledTerminal = job && TERMINAL_STATUSES.has(job.status) ? job : null
  const resultStatus: JobStatus | null =
    (finished?.status as JobStatus | undefined) ?? polledTerminal?.status ?? null
  const resultJobId = finished?.jobId ?? polledTerminal?.id ?? null
  const jobPayload = (polledTerminal?.payload ?? {}) as Record<string, unknown>
  const resultSlug = finished?.slug || (jobPayload.slug as string) || ''
  const resultHostname = finished?.hostname || (jobPayload.public_hostname as string) || ''
  const resultError = finished?.error ?? polledTerminal?.error ?? null
  const failedStep = polledTerminal?.in_flight_step ?? null

  const showResult =
    resultStatus != null && resultJobId != null && dismissedJobId !== resultJobId

  function dismiss() {
    if (resultJobId != null) setDismissedJobId(resultJobId)
    onDismiss?.()
  }

  if (showResult) {
    return (
      <ResultCard
        status={resultStatus as JobStatus}
        slug={resultSlug}
        hostname={resultHostname}
        error={resultError}
        failedStep={failedStep}
        onDismiss={dismiss}
        onRetry={() => {
          ztnaApi
            .retryJob(resultJobId as number)
            .then(() => {
              queryClient.invalidateQueries({ queryKey: ['ztna', 'jobs'] })
              notifications.show({ color: 'teal', title: 'Retry submetido', message: 'Novo job criado.' })
              dismiss()
            })
            .catch(() =>
              notifications.show({ color: 'red', title: 'Falha no retry', message: 'Tente pelo histórico.' }),
            )
        }}
      />
    )
  }

  // Espera por aprovacao NAO e' progresso: sem esta guarda o job cai no banner
  // teal com barra e spinner, rotulado "Provisionando" (o ternario abaixo so
  // distingue `pending`), e o operador ve trabalho acontecendo onde nada
  // acontece. Tambem nao e' terminal — o scheduler o promove quando a GMUD e'
  // aprovada, ou o cancela se ela for rejeitada.
  if (job && job.status === 'pending_gmud') {
    return <AwaitingGmudCard jobId={job.id} gmudId={job.gmud_id ?? null} slug={
      (job.payload as Record<string, unknown> | undefined)?.slug as string | undefined
    } />
  }

  if (!job || TERMINAL_STATUSES.has(job.status)) {
    return null
  }

  const progress = job.progress as Record<string, unknown> | null
  const stepName = (progress?.step as string) || job.in_flight_step || 'inicializando'
  const percent = (progress?.percent as number) ?? 0
  const slug = (job.payload as Record<string, unknown> | undefined)?.slug as string | undefined
  const startedAt = job.started_at ? new Date(job.started_at) : null
  const elapsedSinceStart = startedAt ? Math.floor((Date.now() - startedAt.getTime()) / 1000) : null

  return (
    <div
      data-testid="ztna-job-banner"
      style={{
        background: 'linear-gradient(135deg, #025468 0%, #098d93 100%)',
        color: '#fff',
        padding: '18px 22px',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(2, 84, 104, 0.18)',
        marginBottom: 24,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap={12} align="center" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Loader color="white" size="sm" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text fw={700} size="md">
              {job.status === 'pending' ? 'Aguardando worker iniciar' : 'Provisionando'}
              {slug ? ` ${slug}` : ''}
            </Text>
            <Text size="xs" opacity={0.92}>
              Job #{job.id}
              {elapsedSinceStart != null ? ` · iniciado há ${formatDuration(elapsedSinceStart)}` : ''}
              {' · estimativa total ≤ 6min'}
            </Text>
          </div>
        </Group>
        <Button
          size="xs"
          variant="outline"
          color="white"
          leftSection={<IconPlayerStop size={14} />}
          onClick={() => cancelMut.mutate(job.id)}
          loading={cancelMut.isPending}
        >
          Cancelar
        </Button>
      </Group>

      <Progress
        value={percent}
        size="sm"
        color="white"
        mt={12}
        styles={{ root: { background: 'rgba(255,255,255,0.2)' } }}
      />

      {logLines.length > 0 ? (
        <ScrollArea.Autosize
          mah={120}
          mt={12}
          type="auto"
          viewportRef={seguirOFim}
          style={{ background: 'rgba(0,0,0,0.26)', borderRadius: 8, padding: '10px 12px' }}
        >
          {logLines.slice(-8).map((ln, i) => (
            <Text
              key={`${ln.ts}-${i}`}
              size="xs"
              ff="monospace"
              style={{ color: ln.phase === 'started' ? 'rgba(255,255,255,0.82)' : '#9ff0cf' }}
            >
              <span style={{ opacity: 0.6 }}>{formatDuration(ln.elapsed_seconds)}</span>{' '}
              {ln.phase === 'started' ? '→' : '✓'} {ln.message}
            </Text>
          ))}
        </ScrollArea.Autosize>
      ) : (
        <Text size="sm" fw={600} mt={10} style={{ wordBreak: 'break-word' }}>
          {stepName}
        </Text>
      )}
    </div>
  )
}

interface AwaitingGmudCardProps {
  jobId: number
  gmudId: number | null
  slug?: string
}

/**
 * Job parado esperando aprovação de GMUD.
 *
 * Deliberadamente **sem** spinner, sem barra de progresso e sem botão
 * "Cancelar": nenhum dos três diz a verdade aqui. Não há progresso a mostrar,
 * e `cancel_job` responde 409 `cannot_cancel` para `pending_gmud` — quem errou
 * o pedido sai pela rejeição da GMUD, não por este botão.
 *
 * Âmbar, e não teal: teal é a cor de "provisionando" neste dashboard, e reusá-la
 * aqui seria repetir em cor a mentira que este card existe para desfazer.
 */
function AwaitingGmudCard({ jobId, gmudId, slug }: AwaitingGmudCardProps) {
  return (
    <div
      data-testid="ztna-job-awaiting-gmud"
      style={{
        background: 'rgba(230,173,74,0.10)',
        border: '1px solid #e6ad4a',
        borderLeft: '3px solid #e6ad4a',
        borderRadius: 8,
        padding: '16px 18px',
        marginBottom: 24,
      }}
    >
      <Group align="flex-start" wrap="nowrap" gap={14}>
        <IconClock size={30} color="#e6ad4a" style={{ flex: 'none', marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text fw={700} size="md" style={{ color: '#e6ad4a' }}>
            Aguardando aprovação da mudança
          </Text>
          <Text size="sm" c="dimmed">
            {slug ? `${slug}: ` : ''}o pedido foi registrado e{' '}
            <strong>nada foi provisionado ainda</strong>. A execução começa
            quando a GMUD for aprovada.
          </Text>
          <Text size="xs" c="dimmed" mt={8}>
            Job #{jobId}
            {gmudId != null ? ` · GMUD #${gmudId}` : ''}
          </Text>
        </div>
      </Group>
    </div>
  )
}

interface ResultCardProps {
  status: JobStatus
  slug: string
  hostname: string
  error: string | null
  failedStep: string | null
  onDismiss: () => void
  onRetry: () => void
}

const STEP_LABELS: Record<string, string> = {
  acm_request: 'Solicitação do certificado (ACM)',
  acm_validation_dns: 'Validação de domínio (DNS)',
  acm_validated: 'Emissão do certificado (ACM)',
  route53_a_record: 'Publicação de DNS (Route53)',
  alb_listener_rule: 'Regra de roteamento (ALB)',
  db_insert: 'Registro da aplicação',
  smoke_test: 'Teste de fumaça',
}

function ResultCard({ status, slug, hostname, error, failedStep, onDismiss, onRetry }: ResultCardProps) {
  const ok = status === 'success'
  const needsVal = status === 'needs_validation'
  const accent = ok ? '#2f9e44' : needsVal ? '#e6ad4a' : '#e03131'
  const bg = ok ? 'rgba(47,158,68,0.10)' : needsVal ? 'rgba(230,173,74,0.10)' : 'rgba(224,49,49,0.10)'
  const title = ok
    ? 'Domínio no ar'
    : needsVal
      ? 'Provisionado — validação pendente'
      : status === 'cancelled'
        ? 'Provisionamento cancelado'
        : 'Provisionamento falhou'
  const Icon = ok ? IconCircleCheck : needsVal ? IconClock : IconAlertTriangle

  return (
    <div
      data-testid="ztna-job-result"
      style={{
        background: bg,
        border: `1px solid ${accent}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 8,
        padding: '16px 18px',
        marginBottom: 24,
      }}
    >
      <Group align="flex-start" wrap="nowrap" gap={14}>
        <Icon size={30} color={accent} style={{ flex: 'none', marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text fw={700} size="md" style={{ color: accent }}>
            {title}
          </Text>
          {ok ? (
            <>
              <Text size="sm" c="dimmed">
                {slug ? `${slug} ` : ''}provisionado com sucesso.
              </Text>
              {hostname ? (
                <Group gap={8} mt={10}>
                  <Code>{`https://${hostname}`}</Code>
                  <Anchor href={`https://${hostname}`} target="_blank" rel="noreferrer" size="sm">
                    abrir ↗
                  </Anchor>
                </Group>
              ) : null}
            </>
          ) : status === 'cancelled' ? (
            <Text size="sm" c="dimmed">
              Rollback dos recursos AWS executado.
            </Text>
          ) : (
            <>
              <Text size="sm" c="dimmed">
                {failedStep ? `Parou em: ${STEP_LABELS[failedStep] ?? failedStep}. ` : ''}
                {needsVal ? 'Ação manual pode ser necessária.' : 'Rollback dos recursos AWS executado.'}
              </Text>
              {error ? (
                <Text size="xs" c="red" mt={8} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {error}
                </Text>
              ) : null}
            </>
          )}
          <Group gap={8} mt={14}>
            {!ok && status !== 'cancelled' && status !== 'needs_validation' ? (
              <Button size="xs" color="red" variant="filled" onClick={onRetry}>
                Tentar novamente
              </Button>
            ) : null}
            <Button size="xs" variant="default" onClick={onDismiss}>
              Fechar
            </Button>
          </Group>
        </div>
      </Group>
    </div>
  )
}

export default JobBanner
